/**
 * 작성자 : 한영광
 * 날짜 : 2025.05.27
 * 기능 : (사내)업무관리-결재관리-기안관리,결재내역
 */

import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import GetFetchStatus from '../../../hooks/GetFetchStatus';
import ApplicationFormItems from '../../c_setting/setting7/ApplicationFormItems';
import styled from 'styled-components';
import { useSetAuth } from '../../../utils/useSetAuth';
import { BtnBlue, BtnGhost, BtnLightBlue, BtnRed, BtnYellow } from '../../../components/Button';
import { useTranslation } from 'react-i18next';
import IssueGuide from '../../../components/IssueGuide';
import TextInfoBox from '../../../components/TextInfoBox';
import SmallTextLabel from '../../../components/button/SmallTextLabel';
import Portal from '../../../components/Portal';
import ApprovalModal from '../../../components/Modal/ApprovalModal';
import MultiSelect from '../../../components/MultiSelect';
import { DraftD, IDraft } from 'customTypes';
import { renderApprovalStatus, renderLineStatus } from '../../../utils/renderApprovalStatus';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../../services/_common';
import { toast } from 'react-toastify';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import { isImageValid } from '../../../utils/isImageValid';
import { PrintContextConsumer, useReactToPrint } from 'react-to-print';
import ReplyRow from './ReplyRow';
import { logPost } from '../../../services/log';
import { isMobile } from '../../../utils/checkMobile';
import { isValidYYYYMMDD } from '../../../utils/formatDate';
import { getIsStackedFormLayout, getStackedCells, isStackedAutoHeight } from '../../c_setting/setting7/stackedFormLayout';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';

interface Cell {
  cdName: string;
  width: number;
  height: number;
  aRow: number | null;
  aCol: number | null;
  isDropped: boolean; // Drop 여부
  subCd: string;
  aCd: string;
  aContents: string;
}

interface IUserType {
  label: string;
  value: string;
}

interface DropZoneProps {
  cells: Cell[];
  setCells: React.Dispatch<React.SetStateAction<Cell[]>>;
  logo: string;
  rowState: IDraft;
  userInput: any;
  setUserInput: React.Dispatch<React.SetStateAction<any>>;
  approvalType: 'approval' | 'request';
  isSaveClicked: boolean;
  selectedAttD1List: { label: string; value: string }[];
  className?: string;
  editorContent: string;
  setEditorContent: React.Dispatch<SetStateAction<string>>;
}

const GRID_COL_COUNT = 3;
const CELL_HEIGHT = 46;
const PENDING_APPROVAL = '1'; // 보류
const APPROVED = '2'; // 승인
const REJECTED = '3'; // 반려
const WAITING = '0'; // 결재 대기
// 승인·반려한 결재자는 다음 결재자가 처리하기 전까지 수정 버튼으로 다시 처리할 수 있다 (보류는 이미 본인 차례라 제외)
const REVISABLE_LSTATUS = [APPROVED, REJECTED];
const INIT_ROW = {
  wDate: '',
  eDate: '',
  cCd: '',
  caCd: '',
  status: '',
  name: '',
  department: '',
  delYn: '',
  writer: '',
  editor: '',
  cdraftD1: [],
  cdraftD2: [],
  date: '',
  title: '',
  content: '',
  note: '',
  currentUserId: '',
  tempSaveYn: 'N' as 'Y' | 'N',
};

// 실제 데이터가 있는 행의 수를 계산
const calculateActualRowCount = (cells: Cell[]): number => {
  const droppedCells = cells.filter((cell) => cell.isDropped);
  if (droppedCells.length === 0) return 0;

  // 가장 마지막 행 위치 찾기 (aRow가 null이 아닌 셀들 중)
  const maxRow = Math.max(...droppedCells.map((cell) => (cell.aRow !== null ? cell.aRow + cell.height : 0)));
  return maxRow;
};

// 인쇄 시 그리드 행 높이 계산. 기안내용 에디터(CKEditor 기반, subCd 18/21)가 차지하는 행만 auto로 풀어서
// 내용 길이만큼 늘어나게 하고, 이후 행(참고사항 등)은 그만큼 자연스럽게 아래로 밀려나도록 함.
// 회사별 폼 설정(formSet)에 따라 기안내용 필드가 subCd 18 또는 21로 다르게 배치될 수 있어 둘 다 대상으로 함.
const CONTENT_EDITOR_SUB_CDS = ['18', '21'];
const DRAFT_DATE_SUB_CD = '22'; // 기안서 시행일자 필드
const isContentEditorCell = (cell?: Cell) => !!cell && CONTENT_EDITOR_SUB_CDS.includes(cell.subCd);
const buildGridTemplateRows = (cells: Cell[], rowCount: number, className: string): string => {
  if (className !== 'print') return `repeat(${rowCount}, ${CELL_HEIGHT}px)`;

  const contentCells = cells.filter((cell) => cell.isDropped && isContentEditorCell(cell) && cell.aRow !== null);
  const rows = Array.from({ length: rowCount }, () => `${CELL_HEIGHT}px`);
  contentCells.forEach((contentCell) => {
    if (contentCell.aRow === null) return;
    for (let row = contentCell.aRow; row < contentCell.aRow + contentCell.height && row < rowCount; row += 1) {
      if (row === contentCell.aRow) {
        rows[row] = 'auto'; // 실제 내용이 들어가는 시작 행: 늘어나야 함
      } else if (!cells.some((c) => c.isDropped && c.aRow === row && c !== contentCell)) {
        // 에디터가 시각적으로만 겹쳐 차지하던 예약 행(다른 필드가 없는 빈 행)은 접어서
        // 다음 필드(참고사항 등)가 늘어난 내용 바로 뒤에 이어지도록 함
        rows[row] = '0px';
      }
    }
  });
  return rows.join(' ');
};

const DropZone: React.FC<DropZoneProps> = ({
  cells,
  setCells,
  logo,
  rowState,
  userInput,
  setUserInput,
  approvalType,
  isSaveClicked,
  selectedAttD1List,
  className = '',
  editorContent = '',
  setEditorContent,
}) => {
  const actualRowCount = calculateActualRowCount(cells); // 실제 행 수 계산
  const rowCount = actualRowCount > 0 ? actualRowCount : 1; // 최소 1행은 보여주기
  // 모바일(화면 폭 767px 이하)은 항목을 한 줄에 하나씩 배치. 인쇄본은 항상 격자 배치
  const [isStacked] = useState(() => className !== 'print' && getIsStackedFormLayout());

  if (isStacked) {
    return (
      <div className='dropzone-stack' style={{ display: 'flex', flexDirection: 'column', padding: '0.5rem', border: '1px solid lightgray', borderRadius: '3px', margin: '0.2rem' }}>
        {getStackedCells(cells, rowCount, GRID_COL_COUNT).map((cell) => (
          <DraggableCell
            key={`${cell.aRow}_${cell.aCol}`}
            cell={cell}
            setCells={setCells}
            logo={logo}
            rowState={rowState}
            userInput={userInput}
            setUserInput={setUserInput}
            approvalType={approvalType}
            isSaveClicked={isSaveClicked}
            selectedAttD1List={selectedAttD1List}
            className={className}
            editorContent={editorContent}
            setEditorContent={setEditorContent}
            stacked
          />
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${GRID_COL_COUNT}, 33.3%)`,
        gridTemplateRows: buildGridTemplateRows(cells, rowCount, className),
        padding: '0.5rem',
        overflowY: className === 'print' ? 'visible' : 'auto',
        border: `${className === 'print' ? '1px solid #18181b' : '1px solid lightgray'}`,
        borderBottom: `${className === 'print' ? '2px solid #18181b' : '1px solid lightgray'}`,
        borderRadius: `${className === 'print' ? '0' : '3px'}`,
        margin: '0.2rem',
      }}
    >
      {Array.from({ length: GRID_COL_COUNT * rowCount }).map((_, index) => {
        const row = Math.floor(index / GRID_COL_COUNT); // 열 개수를 기준으로 계산
        const col = index % GRID_COL_COUNT; // 열 인덱스 계산
        const cell = cells.find((c) => c.aRow === row && c.aCol === col);
        // 인쇄 시 기안내용 에디터 셀의 placeholder도 auto로 풀어야
        // 이 셀이 차지하는 grid 행(track)이 실제 내용 높이를 반영해서 늘어남
        const isPrintContentCell = className === 'print' && isContentEditorCell(cell);
        return (
          <div
            key={index}
            style={{
              width: '100%',
              height: isPrintContentCell ? 'auto' : `${CELL_HEIGHT}px`,
              position: 'relative',
            }}
          >
            {cell && (
              <DraggableCell
                cell={cell}
                setCells={setCells}
                logo={logo}
                rowState={rowState}
                userInput={userInput}
                setUserInput={setUserInput}
                approvalType={approvalType}
                isSaveClicked={isSaveClicked}
                selectedAttD1List={selectedAttD1List}
                className={className}
                editorContent={editorContent}
                setEditorContent={setEditorContent}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const DraggableCell: React.FC<{
  cell: Cell;
  setCells: React.Dispatch<React.SetStateAction<Cell[]>>;
  logo: string;
  rowState: IDraft;
  userInput: any;
  setUserInput: Dispatch<SetStateAction<any>>;
  approvalType: 'approval' | 'request';
  isSaveClicked: boolean;
  selectedAttD1List: { label: string; value: string }[];
  className?: string;
  editorContent: string;
  setEditorContent: Dispatch<SetStateAction<string>>;
  stacked?: boolean; // 모바일 한 줄 배치 여부
}> = ({ cell, setCells, logo, rowState, userInput, setUserInput, approvalType, isSaveClicked, selectedAttD1List, className, editorContent, setEditorContent, stacked = false }) => {
  // 인쇄 시 기안내용 에디터는 고정 높이를 풀어 내용 길이만큼 늘어나도록 함 (부모 그리드 행도 auto로 맞춰져 있음)
  const gridHeight = className === 'print' && isContentEditorCell(cell) ? 'auto' : `${CELL_HEIGHT * cell.height}px`;
  // 한 줄 배치: 폭 전체 사용, 입력·글자 항목은 내용 높이만큼(나머지는 지정 높이 유지)
  const height = stacked && isStackedAutoHeight(cell.subCd) ? 'auto' : gridHeight;
  return (
    <div
      className={`draggable-cell subcd_${cell.subCd}`}
      style={{
        position: 'relative',
        width: stacked ? '100%' : cell.isDropped ? `${cell.width * 100}%` : `calc((100% / ${GRID_COL_COUNT}) * ${cell.width})`, // 동적으로 width 계산
        // maxWidth: `${cell.width === 3 ? `calc(${cell.width * 100}% - 2rem)` : `${cell.width * 100}%`}`,
        height,
        display: 'flex',
        // justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontWeight: 'bold',
        // zIndex: 1,
      }}
    >
      {/** 근태 처리 상태가 상신이면서 근태신청 컴포넌트인 경우에만 수정 가능 */}
      {ApplicationFormItems(
        {
          type: rowState.status === '0' && approvalType === 'request' ? 'edit' : 'view',
          data: rowState,
          formType: 'cdraft',
        },
        cell.subCd,
        cell.cdName,
        cell,
        setCells,
        logo,
        userInput,
        setUserInput,
        isSaveClicked,
        selectedAttD1List,
        stacked ? 'stacked' : className, // 항목 스타일(ApplicationFormItems Root)에 한 줄 배치 클래스 전달
        editorContent,
        setEditorContent
      )}
    </div>
  );
};

interface IForm {
  approvalType: 'request' | 'approval';
  rowState: IDraft;
  setRowState: Dispatch<SetStateAction<IDraft>>;
  isNewAdd: boolean;
  setIsNewAdd: Dispatch<SetStateAction<boolean>>;
  tableState: IDraft[] | null;
  setInitTableState: Dispatch<SetStateAction<IDraft[] | null>>;
  formatInTuiForm: any;
}

interface IFile {
  file: File;
  fileName: string;
  fileFormat: string;
  fUrl: string;
  fIndex: number;
}

const MAX_SELECT_COUNT = 6; // 결재라인 최대 선택 가능한 개수
const S3_URL = process.env.REACT_APP_AWS_ROOT_URL;
const DIRECTORY_NAME = process.env.REACT_APP_AWS_DIRECTORY_NAME;
const mobile = isMobile();

const Form = ({ approvalType, rowState, setRowState, isNewAdd, setIsNewAdd, tableState, setInitTableState, formatInTuiForm }: IForm) => {
  const statusKey = 'status';
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const userInfo = useRecoilValue(userState);
  const queryClient = useQueryClient();
  const [cells, setCells] = useState<Cell[]>([]);
  const [logo, setLogo] = useState<string>('');
  const [openModal, setOpenModal] = useState({ type: '', status: false });
  const [users, setUsers] = useState<{ label: string; value: string }[]>([]); // 사내 전체 유저
  const [selectedDraftD1List, setSelectedDraftD1List] = useState<{ label: string; value: string }[]>([]); // 결재라인 선택목록
  const [selectedDraftD2List, setSelectedDraftD2List] = useState<{ label: string; value: string }[]>([]); // 수신참조 선택목록

  const { data: formSet, isError: formSetError } = GetFetchStatus({ path: `/cdraft/set`, req: { cCd: userInfo.hCd }, useLoginUserInfo: false });
  const { data: cInfo } = GetFetchStatus({ path: '/company/info/one', req: { cCd: userInfo.hCd }, useLoginUserInfo: false });
  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false); // 필수값 여부
  const [isApprovalSubmitting, setIsApprovalSubmitting] = useState(false);
  const approvalSubmittingRef = useRef(false);
  const [isRevisingApproval, setIsRevisingApproval] = useState(false); // 이미 처리한 결재를 다시 처리하는 중인지
  const [userInput, setUserInput] = useState({
    wDate: '',
    eDate: '',
    cCd: '',
    caCd: '',
    status: '',
    name: '',
    department: '',
    delYn: '',
    writer: '',
    editor: '',
    date: '',
    title: '',
    content: '',
    note: '',
  });
  const [editorContent, setEditorContent] = useState('');
  // 인쇄용(숨김) DropZone은 화면에 보이는 폼과 동시에 마운트되어 있어, 동일한 setUserInput을 공유하면
  // 두 인스턴스의 DatePickerComponent가 각자 setUserInput을 호출하며 서로의 값을 끝없이 되돌려 쓰는 무한 루프가 발생함.
  // 인쇄용은 읽기 전용 미러이므로 상태를 되돌려 쓸 필요가 없어 no-op으로 대체함.
  const noopSetUserInput = () => {};
  const { data: draftComments } = GetFetchStatus({
    path: `/cdraft/comment`,
    req: { cCd: userInfo.hCd, caCd: rowState?.caCd },
    queryKey: rowState?.caCd,
    useLoginUserInfo: false,
    enabledKey: rowState.caCd !== '',
  });

  const { data: cUserList, isLoading: cUserListLoading } = useQuery(['/user/company', userInfo.hCd], () => fetchCompanyUser(), {
    enabled: !!userInfo.hCd && userInfo.hCd?.substring(0, 1)?.toLowerCase() === 'c',
  });

  const fetchCompanyUser = async () => {
    try {
      const res = await apiGet({ path: '/user/company', req: { hCd: userInfo.hCd } });
      const newMap = res.data.data
        .filter((el: any) => el.uWorkstatus === 'I')
        .map((el: any) => ({ label: el.userName, value: el.userId, positionName: el.positionName, partName: el.partName }))
        .filter((el2: any) => el2.value !== userInfo.userId);
      setUsers(newMap);
      return newMap;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  useEffect(() => {
    setIsRevisingApproval(false);
  }, [rowState.caCd]);

  /** 결재 처리 후에는 문서 번호(caCd)가 같아도 결재선의 상태·일자가 바뀐다.
      이 값까지 의존성에 넣어야 결재 직후 결재란이 새 정보로 다시 그려진다 */
  const approvalLineSnapshot = (rowState.cdraftD1 ?? []).map((line: DraftD) => `${line.userId}:${line.lstatus}:${line.date}`).join('|');

  useEffect(() => {
    if (isSaveClicked) setIsSaveClicked(false);

    let isCancelled = false;

    const fetchData = async () => {
      const { list1, list2 } = await getApprovalLineUsers(rowState);
      if (!isCancelled) {
        setSelectedDraftD1List(list1 ?? []);
        setSelectedDraftD2List(list2 ?? []);
        setEditorContent(rowState.content);
      }
    };
    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [rowState.caCd, approvalLineSnapshot, cUserList]);

  const getApprovalLineUsers = async (row: IDraft) => {
    const approvalLineKey = 'cdraftD1';
    const approvalLineKey2 = 'cdraftD2';

    const insertSignPromises = row[approvalLineKey]?.map(async (el: DraftD) => {
      const isValid = await isImageValid(`${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${el.userId}.png`);
      return {
        label: el.userName,
        value: el.userId,
        date: el.date,
        caLstatus: el.lstatus,
        partName: el.partName,
        positionName: el.positionName,
        signImg: isValid ? `${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${el.userId}.png` : '',
      };
    });

    const insertSign = await Promise.all(insertSignPromises);
    const list2 = row[approvalLineKey2]?.map((el: DraftD) => ({
      label: el.userName,
      value: el.userId,
      date: el.date,
      caLstatus: el.lstatus,
      positionName: el.positionName,
    }));

    return { list1: insertSign, list2 };
  };

  useEffect(() => {
    if (cInfo) setLogo(cInfo[0]?.cLogoimage);
  }, [cInfo]);

  useEffect(() => {
    if (formSet) {
      const set = formSet.map((el: Cell) => ({ ...el, isDropped: el.aCol !== null && el.aRow !== null, height: Number(el.height), width: Number(el.width) }));
      setCells(set);
    }
  }, [formSet]);

  const checkDraftMandatoryField = () => {
    if (userInfo.gCd?.substring(0, 1)?.toLowerCase() !== 'c') {
      toast.warning(t('등록된 사내유저만 이용 가능합니다.'));
      return false;
    }

    if (rowState[statusKey] !== '0') {
      toast.warning(t('상신 상태에서만 수정 가능합니다.'));
      return false;
    }

    if (selectedDraftD1List.length === 0) {
      toast.warning(t('결재 라인을 선택하세요'));
      return false;
    }

    // 시행일자 항목이 배치된 양식만 검사 (서버는 YYYYMMDD 8자리가 아니면 거절)
    const hasDateCell = cells.some((cell) => cell.isDropped && cell.subCd === DRAFT_DATE_SUB_CD);
    if (hasDateCell && !isValidYYYYMMDD((userInput.date || '').replaceAll('-', ''))) {
      toast.warning(t('시행일자를 입력하세요'));
      return false;
    }

    return true;
  };

  const onClickSaveDraft = async (type: 'draft' | 'submit') => {
    setIsSaveClicked(true);

    if (checkDraftMandatoryField()) {
      try {
        const { name, department, title, note, content, date } = userInput;
        const cdraftD1 = selectedDraftD1List.map((el, i) => ({ seq: i.toString(), userId: el.value }));
        const cdraftD2 = selectedDraftD2List.map((el) => ({ userId: el.value }));
        const req = {
          // 수정인 경우 caCd 포함
          ...(rowState.caCd && { caCd: rowState.caCd }),
          cCd: userInfo.hCd,
          name,
          department,
          date: date.split('-').join(''),
          title,
          content,
          note,
          cdraftD1,
          cdraftD2,
          tempSaveYn: type === 'draft' ? 'Y' : 'N',
        };
        let res;
        if (rowState.caCd) res = await apiPatch({ path: '/cdraft', req }); // 업데이트
        else res = await apiPost({ path: '/cdraft', req }); // 신규 등록

        // 서버가 거절한 경우(필수값·형식 오류 등) 아무 표시 없이 끝나지 않도록 서버 메시지 안내
        if (res?.data?.statusCode !== 200) {
          const message = res?.data?.message;
          toast.warning(t(Array.isArray(message) ? message.join(', ') : message || '저장 실패'));
          return;
        }

        if (res.data.statusCode === 200) {
          toast.success(t(res.data.message));
          setInitTableState(formatInTuiForm(res.data.data));
          // 신규등록 직후 rowState 업데이트
          if (isNewAdd) {
            setRowState({ ...res.data.data[0] });
            setIsNewAdd(false);
            setIsSaveClicked(false);
          }
          await logPost({
            hCd: userInfo.hCd,
            sCd: userInfo.sCd,
            userId: userInfo.userId,
            menu: '기안 관리 > 기안 신청',
            action: '저장',
            etc: `기안서 제목 : ${title}`,
          });
        }
      } catch (error) {
        console.error(error);
        throw new Error('error');
      }
    }
  };

  const sortedApprovalLine: DraftD[] = [...(rowState.cdraftD1 ?? [])].sort((a, b) => Number(a.seq) - Number(b.seq));
  const myLineIndex = sortedApprovalLine.findIndex((line) => line.userId === userInfo.userId);
  const myLine = sortedApprovalLine[myLineIndex];
  /** 내가 승인·반려한 뒤 다음(상위) 결재자가 아직 아무도 처리하지 않았으면 다시 처리할 수 있다.
      최종 결재자는 다음 결재자가 없으므로 본인이 처리했으면 언제든 해당한다. 서버도 같은 기준으로 검사한다 */
  const canReviseApproval =
    approvalType === 'approval' && !!myLine && REVISABLE_LSTATUS.includes(myLine.lstatus) && sortedApprovalLine.slice(myLineIndex + 1).every((line) => line.lstatus === WAITING);
  const isMyApprovalTurn = rowState.currentUserId === userInfo.userId;
  // 현재 내 결재 상태와 같은 버튼은 서버가 중복 요청으로 거절하므로 막는다 (수정 중일 때, 보류 후 내 차례가 남아 있을 때)
  const myCurrentLstatus = myLine?.lstatus;

  const onClickApproval = async (param: '0' | '1' | '2' | '3') => {
    if (approvalSubmittingRef.current) return;

    const saveStatusKey = 'lstatus';
    // 가장 큰 seq 값을 가진 결재자가 최종 결재권자
    const isFinalApprover = sortedApprovalLine[sortedApprovalLine.length - 1]?.userId === userInfo.userId;

    approvalSubmittingRef.current = true;
    setIsApprovalSubmitting(true);
    try {
      const req = {
        cCd: rowState.cCd,
        caCd: rowState.caCd,
        userId: rowState.writer, // 상신자
        [saveStatusKey]: param,
        finalApproval: isFinalApprover ? '1' : '0', // 최종 결재권자 여부
        // 요청 시점의 상태. 그사이 다른 사람이 먼저 처리했으면 서버가 409로 거절한다
        ...(rowState.status && { status: rowState.status }),
        ...(myLine?.lstatus !== undefined && { expectedLstatus: myLine.lstatus }),
      };
      const res = await apiPatch({ path: `/cdraft/history`, req });
      // 403: 결재 차례가 아니거나 다음 결재자가 이미 처리함, 409: 그사이 상태가 바뀜
      if ([403, 409].includes(res?.status) || [403, 409].includes(res?.data?.statusCode)) {
        const message = res.data?.message;
        toast.warning(t(Array.isArray(message) ? message.join(', ') : message || '이미 처리된 결재 요청입니다. 최신 상태를 확인해 주세요.'));
        return;
      }
      if (res.data.statusCode === 200) {
        setInitTableState(formatInTuiForm(res.data.data));
        const find = res.data.data.find((el: IDraft) => el.caCd === rowState?.caCd);
        if (find) {
          // 상신자 서명정보
          const isValidSignImg = await isImageValid(`${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${find.writer}.png`);
          const randomString = Math.random().toString(36).substring(7); // 랜덤 문자열을 쿼리스트링으로 추가해서 이미지 캐싱 방지
          setRowState({ ...find, signImg: isValidSignImg ? `${S3_URL}${DIRECTORY_NAME}${userInfo.hCd}/sign/${find.writer}.png?v=${randomString}` : '' });
          setIsRevisingApproval(false);
          toast.success(t(`${renderLineStatus(param).text} 되었습니다`));
        }
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '기안 관리 > 기안 결재',
          action: `${renderLineStatus(param).text} 처리`,
          etc: `기안서 제목 : ${rowState.title}`,
        });
        // 사이드메뉴 결재 알림 쿼리 무효화하여 다시 가져오기 (AsideMainMenu)
        queryClient.invalidateQueries({ queryKey: ['approval_cdraft_approval', userInfo.hCd] });
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    } finally {
      approvalSubmittingRef.current = false;
      setIsApprovalSubmitting(false);
    }
  };

  const deleteAPI = async () => {
    try {
      const req = { cCd: userInfo.hCd, caCd: rowState.caCd };
      const res = await apiDelete({ path: `/cdraft`, req });
      if (res.data.statusCode === 200) {
        toast.success(t(res.data.message));
        setIsNewAdd(true);
        setRowState(INIT_ROW);
        const result = tableState?.filter((el) => el.caCd !== req.caCd) ?? [];
        setInitTableState(formatInTuiForm(result));
        await logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '기안 관리 > 기안 신청',
          action: '기안 삭제',
          etc: `기안서 제목 : ${rowState.title}`,
        });
      }
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickDelete = async () => {
    // 업무 보고는 결재 진행 프로세스가 따로 없으므로 제외
    if (rowState && rowState[statusKey] !== '0') {
      toast.warning(t('결재 진행 중입니다.'));
      return;
    }

    setOpenModal((prev) => ({ ...prev, status: true, type: 'delete', api: deleteAPI }));
  };

  const getLineList = (list1: string[], list2: string[]) => {
    const newList1 = list1.map((el) => users.find((u) => u.value === el)).filter((el2): el2 is IUserType => el2 !== null && el2 !== undefined);
    const newList2 = list2.map((el) => users.find((u) => u.value === el)).filter((el2): el2 is IUserType => el2 !== null && el2 !== undefined);

    setSelectedDraftD1List((prev) => {
      const setFilter = new Set([...prev, ...newList1]); // 합친 이후 중복 제거
      return [...setFilter];
    });
    setSelectedDraftD2List((prev) => {
      const setFilter = new Set([...prev, ...newList2]); // 합친 이후 중복 제거
      return [...setFilter];
    });
    setOpenModal({ type: '', status: false });
  };

  const onClickPrint = async () => {
    handlePrint();
  };

  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    // react-to-print는 document의 모든 <style>을 인쇄용 iframe에 그대로 복사하는데,
    // GlobalStyle.ts의 전역 `body { overflow: hidden; height: ... }`도 같이 복사되어
    // 인쇄 iframe의 body 자체가 화면 높이로 고정/클리핑되면서 내용이 여러 페이지로 안 나뉘고 잘렸음.
    // 인쇄본에서만 body를 원상복구(overflow visible, height auto)해서 내용 길이만큼 여러 페이지로 나뉘도록 함.
    pageStyle: `@page { size: A4; margin: 10mm; } body { height: auto !important; overflow: visible !important; display: block !important; } .dropzone-wrapper { zoom: 0.9; }`,
  });

  const fileDataSet = (e: ChangeEvent<HTMLInputElement>, set: any) => {
    const { files } = e.target;

    if (files) {
      const arr: number[] = Array.from({ length: files.length }, (_, i) => i);
      const fileOption: IFile[] = arr.map((currentIdx: number) => ({
        fIndex: currentIdx,
        file: files[currentIdx],
        fileName: files[currentIdx].name,
        fileFormat: files[currentIdx].name.slice(files[currentIdx].name.lastIndexOf('.') + 1),
        fUrl: '',
      }));

      set((prev: IFile[]) => [...prev, ...fileOption]);
      e.target.value = '';
    }

    return false;
  };

  const formTypeName = t('기안');

  if (formSetError) return <IssueGuide />;
  if (approvalType === 'approval' && isNewAdd) return <IssueGuide text={`${formTypeName} 항목을 선택하세요`} />;
  if (mobile && cUserListLoading) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }
  return (
    <Root style={{ overflow: !isMobile() ? 'hidden' : 'auto' }}>
      <div className='header flex-flex'>
        <div className='flex-basic'>
          <div className='title'>{t('처리 상태')}</div>
          <SmallTextLabel text={renderApprovalStatus(rowState[statusKey] || '').text} color={renderApprovalStatus(rowState[statusKey] || '').color} />
        </div>
        <div className='flex-end'>
          {approvalType === 'approval' && auth.printAuth && (
            <BtnGhost onClick={onClickPrint}>
              <span className='material-symbols-rounded'>print</span>
              {t('인쇄')}
            </BtnGhost>
          )}
        </div>
      </div>
      {!isMobile() ? (
        <TextInfoBox description='순서의 왼쪽부터 결재가 진행되며, 결재 라인은 총 6개까지만 설정할 수 있습니다.' color='orange' />
      ) : (
        <TextInfoBox description='왼쪽부터 결재 진행, 결재 라인 최대 6명 선택 가능' color='orange' />
      )}
      <div className='header flex-start'>
        <div className='title required'>{t('결재 라인')}</div>
        <div
          className={`flex-basic w100 multi-select ${approvalType === 'approval' || rowState[statusKey] !== '0' ? 'remove_clear' : ''} ${
            isSaveClicked && selectedDraftD1List.length === 0 ? 'getBorder' : ''
          }`}
        >
          {approvalType === 'approval' || rowState[statusKey] !== '0' ? (
            <div className='approval-user-line'>
              {selectedDraftD1List.map((user) => (
                <span key={user.value} className='tag'>
                  {user.label}
                </span>
              ))}
            </div>
          ) : (
            <>
              <MultiSelect options={users} value={selectedDraftD1List} setVal={setSelectedDraftD1List} placeholder='결재자' maxSelectCount={MAX_SELECT_COUNT} />
              {approvalType === 'request' && rowState[statusKey] === '0' && (
                <div className='button' onClick={() => setOpenModal({ type: 'participants', status: true })} role='button' tabIndex={0}>
                  {t('선택')}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className='header flex-start'>
        <div className='title'>{t('수신 / 참조')}</div>
        <div className={`flex-basic w100 multi-select ${approvalType === 'approval' || rowState[statusKey] !== '0' ? 'remove_clear' : ''}`}>
          {approvalType === 'approval' || rowState[statusKey] !== '0' ? (
            <div className='approval-user-line'>
              {selectedDraftD2List.map((user) => (
                <span key={user.value} className='tag'>
                  {user.label}
                </span>
              ))}
            </div>
          ) : (
            <>
              <MultiSelect options={users} value={selectedDraftD2List} setVal={setSelectedDraftD2List} placeholder='수신/참조자' />
              {approvalType === 'request' && rowState[statusKey] === '0' && (
                <div className='button' onClick={() => setOpenModal({ type: 'participants', status: true })} role='button' tabIndex={0}>
                  {t('선택')}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className='dropzone-wrapper' style={{ overflow: !isMobile() ? 'scroll' : '' }}>
        <div className='dropzone-printable'>
          <DropZone
            cells={cells}
            setCells={setCells}
            logo={logo}
            rowState={rowState}
            userInput={userInput}
            setUserInput={setUserInput}
            approvalType={approvalType}
            isSaveClicked={isSaveClicked}
            selectedAttD1List={selectedDraftD1List}
            editorContent={editorContent}
            setEditorContent={setEditorContent}
          />
        </div>
        {!isNewAdd && draftComments && <ReplyRow rowState={rowState} data={draftComments} endPoint='/cdraft/comment' />}
      </div>
      <PrintContextConsumer>
        {() => (
          <div style={{ display: 'none' }}>
            <div className='dropzone-wrapper print' ref={componentRef}>
              <div className='dropzone-printable'>
                <DropZone
                  cells={cells}
                  setCells={setCells}
                  logo={logo}
                  rowState={rowState}
                  userInput={userInput}
                  setUserInput={noopSetUserInput}
                  approvalType='approval'
                  isSaveClicked={isSaveClicked}
                  selectedAttD1List={selectedDraftD1List}
                  className='print'
                  editorContent={editorContent}
                  setEditorContent={setEditorContent}
                />
              </div>
            </div>
          </div>
        )}
      </PrintContextConsumer>

      {approvalType === 'request' && (auth.createAuth || auth.updateAuth || auth.deleteAuth) && (
        <ButtonsWrapper className='end'>
          {!isNewAdd && auth.deleteAuth && (
            <BtnRed type='button' onClick={onClickDelete}>
              {t('삭제')}
            </BtnRed>
          )}
          {(auth.createAuth || auth.updateAuth) && (
            <>
              {approvalType === 'request' && (rowState.tempSaveYn === 'Y' || isNewAdd) && (
                <BtnLightBlue type='button' onClick={() => onClickSaveDraft('draft')}>
                  {t('임시저장')}
                </BtnLightBlue>
              )}
              <BtnBlue type='button' onClick={() => onClickSaveDraft('submit')}>
                {t('상신')}
              </BtnBlue>
            </>
          )}
        </ButtonsWrapper>
      )}
      {/** 기안결재 컴포넌트이면서 본인의 결재 차례이거나, 이미 처리한 결재를 수정 중인 경우에만 버튼 렌더링.
          currentUserId 는 서버가 내려주는 현재 결재 대기자라, 완료·반려로 종결된 건에는 빈 값이 온다 */}
      {approvalType === 'approval' && (isMyApprovalTurn || isRevisingApproval) && (
        <ButtonsWrapper className='between'>
          <div className='flex-basic'>
            <BtnRed type='button' disabled={isApprovalSubmitting || myCurrentLstatus === REJECTED} onClick={() => onClickApproval(REJECTED)}>
              {t('반려')}
            </BtnRed>
            <BtnYellow type='button' disabled={isApprovalSubmitting || myCurrentLstatus === PENDING_APPROVAL} onClick={() => onClickApproval(PENDING_APPROVAL)}>
              {t('보류')}
            </BtnYellow>
          </div>
          <div className='flex-basic'>
            {isRevisingApproval && (
              <BtnGhost type='button' disabled={isApprovalSubmitting} onClick={() => setIsRevisingApproval(false)}>
                {t('취소')}
              </BtnGhost>
            )}
            <BtnBlue type='button' disabled={isApprovalSubmitting || myCurrentLstatus === APPROVED} onClick={() => onClickApproval(APPROVED)}>
              {t('승인')}
            </BtnBlue>
          </div>
        </ButtonsWrapper>
      )}
      {canReviseApproval && !isMyApprovalTurn && !isRevisingApproval && (
        <ButtonsWrapper className='end'>
          <BtnBlue type='button' onClick={() => setIsRevisingApproval(true)}>
            {t('수정')}
          </BtnBlue>
        </ButtonsWrapper>
      )}
      <Portal openModal={openModal?.status}>
        {openModal.status && openModal.type === 'participants' && <ApprovalModal openModal={openModal} setOpenModal={setOpenModal} lGubun='2' getLineList={getLineList} />}
        {openModal.status && openModal.type === 'delete' && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}
      </Portal>
    </Root>
  );
};

export default Form;

const Root = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  /* overflow: hidden; */
  justify-content: flex-start;

  .header {
    padding: 0.4rem 0.75rem; // 상하 패딩 축소
    height: 2.75rem; // 고정 높이 축소 (패딩만 줄이면 고정 높이 때문에 행이 안 줄어듦)
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: var(--text-primary);
    display: flex;
    gap: 0.5rem;
    &:nth-child(n + 2) {
      border-top: 1px solid var(--outline);
    }
    .title {
      flex-shrink: 0;
      width: fit-content;
      min-width: 4.5rem;
      font-weight: 600;
      margin-right: 0.5rem;
    }

    .file-wrap {
      height: 3.5rem;
      overflow-y: auto;
      border-radius: 0.5rem;
      padding: 0.75rem 0;

      .file-inner {
        display: flex;
        flex-direction: column !important;
        width: 100%;
        gap: 0.5rem;
      }

      .file-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 0 0.5rem;

        &:hover {
          background-color: var(--tonal);
        }
      }

      .file {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        span {
          font-size: 0.875rem;
          color: var(--text-primary);
        }
      }

      .file-btn {
        width: 2rem;
        height: 2rem;
        padding: 0 0.5rem;

        span.material-symbols-rounded {
          font-variation-settings: 'FILL' 1, 'wght' 400;
          font-size: 1.25rem;
        }
      }
    }

    > div > label > .flex-box > .material-symbols-rounded {
      margin-right: 0;
    }

    @media screen and (max-width: 767px) {
      padding: 0.25rem;
    }
  }

  .approval-user-line {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .tag {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 0.5rem;
      height: 28px;
      border-radius: 2.5px;
      color: var(--selected-primary);
      border: 1px solid var(--selected-primary);
      background-color: var(--tonal-blue);
    }
  }

  .tagContainer {
    user-select: none;
    background-color: var(--tonal-light);
    padding: 0.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.2rem 0.5rem;
    width: 100%;
    height: 8rem;
    overflow: auto;
  }

  .button {
    flex-shrink: 0;
    user-select: none;
    cursor: pointer;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--filled-slate-deep);
    color: var(--bw-inverse);
    border-radius: 0.25rem;
    padding: 0 0.75rem;
    margin: 0.5rem;
    font-size: 0.875rem;
    font-weight: 400;
    &:hover {
      outline: 0.25rem solid var(--outline);
      &:active {
        background-color: var(--filled-slate);
      }
    }
  }

  .scroll-container {
    flex-grow: 1;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    overflow-y: hidden;
    white-space: nowrap;
  }

  .multi-select {
    height: 2rem; // 우측 '선택' 버튼(.button height: 2rem)과 높이 통일
    flex-grow: 1;
    min-width: 0; // 필수: flex-grow가 있을 때 오버플로우를 위해 필요

    &.getBorder {
      > :first-child {
        border: solid 2px red !important;
        border-radius: 5px;
      }
    }

    &.remove_clear {
      .select__multi-value__remove {
        display: none !important;
      }
    }

    .select__control {
      box-sizing: border-box; // 테두리(1px)가 높이에 더해져 버튼보다 커지는 것 방지
      // 우측 '선택' 버튼(2rem)과 높이 통일. react-select 기본 control(min-height 38px)/내부 콘텐츠가 32px를 넘기므로 height를 하드 고정.
      // 하드 고정 시 내부 grid가 클램프되어 placeholder가 아래로 밀리는 문제는 아래 value-container를 flex 중앙정렬로 강제해 해결.
      min-height: 2rem;
      height: 2rem;
      max-height: 2rem;
      background-color: var(--tonal-light);
      border-color: var(--outline);

      &:hover {
        border-color: var(--outline);
      }

      .select__placeholder {
        color: var(--text-primary);
        font-size: 0.875rem;
      }
    }

    .select__value-container {
      // react-select 기본은 display:grid라 컨트롤을 2rem로 하드 고정하면 placeholder 세로정렬이 틀어짐.
      // flex로 바꿔 자식(placeholder/태그)을 세로 중앙정렬. (단, placeholder 자체는 flex로 만들지 말 것 - 텍스트가 잘림)
      display: flex;
      align-items: center;
      height: 100%;
      padding: 0 8px; // 상하 패딩 제거: 컨트롤 높이 2rem 안에 태그가 들어가도록
      flex-wrap: nowrap; // 줄바꿈 방지
      overflow-x: auto; // 가로 스크롤 추가
    }

    // placeholder/입력영역을 full-height로 강제하지 않음(강제 시 컨트롤 하드 고정과 겹쳐 placeholder가 아래로 밀림).
    // display는 react-select 기본값 유지(flex로 바꾸면 placeholder 텍스트가 잘림)
    .select__placeholder,
    .select__single-value,
    .select__input-container {
      height: auto;
      margin: 0;
      padding: 0;
    }

    // 드롭다운/클리어 아이콘 패딩 축소: 기본 패딩(8px)+아이콘(20px)=36px라 컨트롤이 2rem을 넘김.
    // react-select 실제 클래스명(dropdown/clear-indicator)으로 타깃해야 적용됨
    .select__indicators {
      height: 2rem;
    }
    .select__indicator,
    .select__dropdown-indicator,
    .select__clear-indicator {
      padding: 2px;
    }
    .select__indicator svg,
    .select__dropdown-indicator svg,
    .select__clear-indicator svg {
      width: 18px;
      height: 18px;
    }
    .select__indicator-separator {
      margin: 4px 0;
    }

    .select__multi-value {
      box-sizing: border-box; // 테두리(1px) 포함 22px로 계산되어 세로 중앙 어긋남 방지
      height: 22px; // 뱃지 높이 축소 (컨트롤 2rem 안에 여유있게)
      display: flex;
      align-items: center;
      align-self: center; // value-container 안에서 세로 중앙 고정
      transform: translateY(-1px); // 형제 입력영역(폰트 14px)과의 높이/베이스라인 차로 뱃지가 미세하게 내려가 보이는 것 광학 보정
      margin: 0 2px;
      flex-shrink: 0; // 태그가 줄어들지 않도록 설정
      color: var(--selected-primary);
      background-color: var(--tonal-blue);
      border-radius: 2.5px;
      border: 1px solid;
    }

    .select__multi-value__label {
      color: var(--selected-primary);
      display: flex;
      align-items: center;
      padding: 1px 6px;
      font-size: 0.75rem; // 뱃지 폰트 축소
      font-weight: 500;
    }

    .select__multi-value__remove {
      display: flex;
      align-items: center;
      padding: 0 4px;
      height: 100%;
      cursor: pointer;
    }

    .button {
      // 상하 margin 제거: .multi-select 행이 버튼 margin(0.5rem)만큼 커져(컨트롤 높이 초과) placeholder가 아래로 밀리는 문제 방지. 좌측 간격만 유지
      margin: 0 0 0 0.5rem;
      height: 2rem;
    }
  }

  .dropzone-wrapper {
    /* overflow: scroll; */

    &.print {
      background-color: #fff;
    }

    @media screen and (max-width: 767px) {
      // 격자 배치용 압축. 한 줄 배치 항목(.stacked)은 ApplicationFormItems 스타일을 따름
      .input:not(.stacked) {
        padding: 0.2rem;

        & .label {
          width: fit-content;
          padding-right: 0.5rem;
        }
      }
    }
  }

  // 한 줄 배치: 격자에서 왼쪽 라벨 칸 자리를 비우려던 에디터 오른쪽 이동(4rem)을 풀고 폭 전체 사용
  .dropzone-stack .subcd_18 > .editor > .editor-wrapper .ck-editor {
    width: 100%;
    right: 0;
  }

  .subcd_18 > .editor > .editor-wrapper {
    .ck-editor {
      width: calc(100% - 4rem);
      right: -4rem;
      border-radius: 6px;
      /* border-radius: 0 6px 6px 0; */

      .ck-content {
        border-radius: 0 0 6px 6px;
      }

      .ck-sticky-panel__content {
        border-radius: 6px 6px 0 0;
      }
    }

    > div {
      .ck-editor__main > div {
        height: 38rem;
      }
    }
  }

  /* 기안내용 필드가 subCd 21(textarea 타입)로 배치된 경우, 제목/참고사항 textarea와 border-radius(6px) 통일 */
  .subcd_21 > .textarea > .editor-wrapper {
    .ck-editor {
      border-radius: 6px;

      .ck-content {
        border-radius: 0 0 6px 6px;
      }

      .ck-sticky-panel__content {
        border-radius: 6px 6px 0 0;
      }
    }
  }

  /* 인쇄본에서만 기안내용 에디터의 고정 높이(38rem)를 풀어 내용 전체가 인쇄되도록 함 */
  .dropzone-wrapper.print .subcd_18 > .editor > .editor-wrapper {
    .ck-editor__main {
      height: auto !important;
    }

    > div {
      .ck-editor__main > div {
        height: auto !important;
        overflow: visible !important;
      }
    }
  }

  /* 회사 폼 설정에 따라 기안내용 필드가 subCd 21(textarea 타입)로 배치된 경우도 동일하게 처리 */
  .dropzone-wrapper.print .subcd_21 > .textarea .editor-wrapper {
    .ck-editor,
    .ck-editor__main,
    .ck-editor__editable {
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
    }
  }
`;

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;

  &.end {
    justify-content: flex-end;
  }

  &.between {
    justify-content: space-between;
    > div {
      gap: 0.5rem;
    }
  }

  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;
